import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Input } from "reactstrap";
import moment from "moment";
import Pagination from "@mui/material/Pagination";
import StudioFilterBar, { readFilterSelect } from "../../common/Components/FilterBar/StudioFilterBar";
import GeoResponsiveList from "../../common/Components/GeoList/GeoResponsiveList";
import StudioRowActions, { StudioRowAction } from "../../common/Components/GeoList/StudioRowActions";
import notifications from "../../common/Components/Notifications/Notification";
import HKPConfirmDialog from "../../common/Components/HKPConfirmDialog";
import { canUseOrders } from "../../helpers/orderEnabled";
import { deleteOrder, duplicateOrder, listOrders, updateOrderStatus } from "../../services/studioOrderApi";
import { geoRecordId, unwrapGeoPayload } from "../gi-registry/geoPath";
import { WORKFLOW_STATUSES, askWorkflowExtras, workflowLabel } from "./orderStatus";
import { approvalChip, isStudioStaff } from "./approval/approvalStatus";
import "../gi-registry/gi-registry.scss";
import "./orders.scss";

const LIMIT = 20;

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const recordClass = (row: any) => {
  if (row?.entryComplete) {
    return "gi-status-chip is-frozen";
  }
  if (row?.entryStarted) {
    return "gi-status-chip is-pending";
  }
  return "gi-status-chip is-change";
};

const recordLabel = (row: any) => {
  if (row?.entryComplete) {
    return "Details filled";
  }
  if (row?.entryStarted) {
    return `Details incomplete · ${row?.completenessPercent || 0}%`;
  }
  return "Details incomplete";
};

const OrdersList = () => {
  const history = useHistory();
  const location = useLocation();
  const authUser: any = readAuthUser();
  const role = authUser?.users?.role;
  const isSuperAdmin = role === "SUPER_ADMIN";
  const canWrite = canUseOrders(role) && !isSuperAdmin;
  const queryStatus = new URLSearchParams(location.search).get("status") || "";
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState(queryStatus);
  const [completeness, setCompleteness] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [totCnt, setTotCnt] = useState(0);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [orderToDelete, setOrderToDelete] = useState<any>(null);
  const deleteInFlight = useRef(false);

  useEffect(() => {
    const next = new URLSearchParams(location.search).get("status") || "";
    setStatus(next);
    setOffset(0);
  }, [location.search]);

  const loadList = useCallback(
    async (
      pageOffset = offset,
      statusFilter = status,
      completenessFilter = completeness,
      q = appliedSearch
    ) => {
      setLoading(true);
      setError("");
      try {
        const res: any = await listOrders({
          offset: pageOffset,
          limit: LIMIT,
          status: statusFilter || undefined,
          completeness: completenessFilter || undefined,
          q: q || undefined,
        });
        if (!res?.success) {
          setError("Unable to load orders. Please try again.");
          notifications({
            title: "Error!",
            message: "Unable to load orders. Please try again.",
            type: "danger",
          });
          return;
        }
        setRows(Array.isArray(res?.data?.docs) ? res.data.docs : []);
        setTotCnt(Number(res?.data?.length) || 0);
      } catch {
        setError("Unable to load orders. Please try again.");
        notifications({
          title: "Error!",
          message: "Unable to load orders",
          type: "danger",
        });
      } finally {
        setLoading(false);
      }
    },
    [offset, status, completeness, appliedSearch]
  );

  useEffect(() => {
    if (!canUseOrders(role)) {
      history.replace("/dashboard");
      return;
    }
    loadList(offset, status, completeness, appliedSearch);
  }, [offset, status, completeness, appliedSearch, role, history, loadList]);

  const handleReset = () => {
    setStatus("");
    setCompleteness("");
    setSearch("");
    setAppliedSearch("");
    setOffset(0);
    if (location.search) {
      history.replace("/orders");
    }
    loadList(0, "", "", "");
  };

  const handlePageChange = (_: unknown, page: number) => {
    setOffset(page - 1);
  };

  const handleDelete = async () => {
    const id = geoRecordId(orderToDelete);
    if (!id || deleteInFlight.current) return;
    deleteInFlight.current = true;
    setBusyId(id);
    try {
      const res: any = await deleteOrder(id);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: "Could not remove this order. Please try again.",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: "Order removed",
        type: "success",
      });
      setOrderToDelete(null);
      loadList(offset, status, completeness, appliedSearch);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not delete this order",
        type: "danger",
      });
    } finally {
      deleteInFlight.current = false;
      setBusyId("");
    }
  };

  const handleStatusChange = async (row: any, next: string) => {
    const id = geoRecordId(row);
    const current = row?.workflowStatus || "RECORDED";
    if (!id || !canWrite || next === current) {
      return;
    }
    const extras = askWorkflowExtras(next);
    if (extras.cancelled) {
      return;
    }
    setBusyId(id);
    try {
      const res: any = await updateOrderStatus(id, {
        workflowStatus: next,
        stuckReason: extras.stuckReason || null,
        customStageLabel: extras.customStageLabel || null,
      });
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not update status",
          type: "danger",
        });
        return;
      }
      const updated = unwrapGeoPayload(res) || {};
      notifications({
        title: "Success!",
        message: `Status: ${updated.workflowStatusLabel || workflowLabel(next, extras.customStageLabel)}`,
        type: "success",
      });
      loadList(offset, status, completeness, appliedSearch);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not update status",
        type: "danger",
      });
    } finally {
      setBusyId("");
    }
  };

  const handleDuplicate = async (row: any) => {
    const id = geoRecordId(row);
    if (!id) {
      return;
    }
    setBusyId(id);
    try {
      const res: any = await duplicateOrder(id);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not duplicate this order",
          type: "danger",
        });
        return;
      }
      const copyId = res?.data?.id || res?.data?._id;
      notifications({
        title: "Success!",
        message: `Duplicated as ${res?.data?.orderCode || "a new order"}`,
        type: "success",
      });
      if (copyId) {
        history.push(`/orders/${copyId}/edit`);
      } else {
        loadList(offset, status, completeness, appliedSearch);
      }
    } catch {
      notifications({
        title: "Error!",
        message: "Could not duplicate this order",
        type: "danger",
      });
    } finally {
      setBusyId("");
    }
  };

  const totPages = Math.ceil(totCnt / LIMIT) || 0;
  const emptyText =
    error && !loading && rows.length === 0
      ? error
      : loading && rows.length === 0
      ? "Loading orders…"
      : "No orders recorded yet. Create a record as soon as the client gives or accepts the order — you can finish details later.";

  const orderLineSummary = (ele: any) =>
    Array.isArray(ele?.lines) && ele.lines.length
      ? ele.lines
          .map((line: any) =>
            [line?.colour, line?.quantity != null ? `×${line.quantity}` : ""]
              .filter(Boolean)
              .join(" ")
          )
          .join(", ")
      : ele?.quantity
      ? `Qty ${ele.quantity}`
      : "";

  const orderStatusCell = (ele: any) => {
    const id = geoRecordId(ele);
    return (
      <>
        {canWrite ? (
          <Input
            type="select"
            className="order-status-select"
            value={ele?.workflowStatus || "RECORDED"}
            disabled={busyId === id}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              handleStatusChange(ele, e.target.value);
            }}
          >
            {WORKFLOW_STATUSES.some((item) => item.value === (ele?.workflowStatus || "RECORDED"))
              ? null
              : (
                <option value={ele?.workflowStatus || "RECORDED"}>
                  {workflowLabel(ele?.workflowStatus, ele?.customStageLabel)}
                </option>
              )}
            {WORKFLOW_STATUSES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </Input>
        ) : (
          <div>
            {ele?.workflowStatusLabel || workflowLabel(ele?.workflowStatus, ele?.customStageLabel)}
          </div>
        )}
        {ele?.stuckReason ? (
          <div className="gi-au-hint" style={{ marginBottom: 0 }}>
            {ele.stuckReason}
          </div>
        ) : null}
        {ele?.customerApproval?.status ? (
          <span className={`order-approval-chip is-${approvalChip(ele.customerApproval).tone}`}>
            {approvalChip(ele.customerApproval).label}
          </span>
        ) : null}
      </>
    );
  };

  const orderActions = (ele: any) => {
    const id = geoRecordId(ele);
    const actions: StudioRowAction[] = [
      {
        key: "view",
        label: "View",
        icon: "view",
        onClick: () => history.push(`/orders/${id}`),
      },
    ];
    if (canWrite) {
      actions.push(
        {
          key: "edit",
          label: "Edit",
          icon: "edit",
          tone: "primary",
          onClick: () => history.push(`/orders/${id}/edit`),
        },
        {
          key: "duplicate",
          label: "Duplicate",
          icon: "duplicate",
          disabled: busyId === id,
          onClick: () => handleDuplicate(ele),
        },
        {
          key: "delete",
          label: "Delete",
          icon: "delete",
          tone: "danger",
          disabled: busyId === id,
          onClick: () => setOrderToDelete(ele),
        }
      );
    }
    return <StudioRowActions actions={actions} />;
  };

  return (
    <div className="gi-registry-page">
      <HKPConfirmDialog
        open={Boolean(orderToDelete)}
        title="Remove order?"
        description={`Remove order ${orderToDelete?.orderCode || ""} from this studio?`}
        confirmLabel="Remove order"
        busy={Boolean(busyId)}
        onCancel={() => setOrderToDelete(null)}
        onConfirm={handleDelete}
      />
      <div className="gi-toolbar-row">
        <h2>Orders</h2>
        <StudioFilterBar
          fields={[
            {
              id: "search",
              label: "Search",
              placeholder: "Search order, customer, design",
              value: search,
              onChange: (value: string) => setSearch(value),
            },
            {
              id: "status",
              label: "Status",
              kind: "select",
              placeholder: "All statuses",
              value: status,
              options: [
                { label: "Production", value: "PRODUCTION" },
                ...WORKFLOW_STATUSES.map((item) => ({ label: item.label, value: item.value })),
              ],
              onChange: (value: any) => {
                const next = readFilterSelect(value);
                setStatus(next);
                setOffset(0);
                history.replace(next ? `/orders?status=${encodeURIComponent(next)}` : "/orders");
              },
            },
            {
              id: "completeness",
              label: "Record",
              kind: "select",
              placeholder: "All records",
              value: completeness,
              options: [
                { label: "Details incomplete", value: "INCOMPLETE" },
                { label: "Details filled", value: "COMPLETE" },
              ],
              onChange: (value: any) => {
                setCompleteness(readFilterSelect(value));
                setOffset(0);
              },
            },
          ]}
          defaultFieldId="search"
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              setAppliedSearch(search.trim());
              setOffset(0);
            }
          }}
          onReset={handleReset}
          addTo={canWrite ? "/orders/new" : undefined}
          addAriaLabel="Record order"
          hideAdd={!canWrite}
          wideToolbar={
            <div className="gi-registry-filters gi-registry-filters--orders">
              <Input
                type="text"
                placeholder="Search order, customer, design"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setAppliedSearch(search.trim());
                    setOffset(0);
                  }
                }}
              />
              <Input
                type="select"
                className="geo-filter-status"
                value={status}
                onChange={(e) => {
                  const next = e.target.value;
                  setStatus(next);
                  setOffset(0);
                  history.replace(next ? `/orders?status=${encodeURIComponent(next)}` : "/orders");
                }}
              >
                <option value="">All statuses</option>
                <option value="PRODUCTION">Production</option>
                {WORKFLOW_STATUSES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Input>
              <Input
                type="select"
                className="geo-filter-status"
                value={completeness}
                onChange={(e) => {
                  setCompleteness(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="">All records</option>
                <option value="INCOMPLETE">Details incomplete</option>
                <option value="COMPLETE">Details filled</option>
              </Input>
              <Button className="btn-hastkala-outline" onClick={handleReset}>
                Reset
              </Button>
              {canWrite && isStudioStaff(role) ? (
                <Button className="btn-hastkala-outline" onClick={() => history.push("/orders/approval-settings")}>
                  Approval links
                </Button>
              ) : null}
              {canWrite ? (
                <Button className="ResetBtn geo-toolbar-cta" onClick={() => history.push("/orders/new")}>
                  Record order
                </Button>
              ) : null}
            </div>
          }
        />
      </div>
      <p className="gi-au-hint">
        Record an order when the client first gives or accepts it. Filling the form is not the same
        as completing production. One studio cannot see another studio&apos;s orders.
      </p>
      <GeoResponsiveList
        rows={rows}
        rowKey={(ele) => geoRecordId(ele) || ele.orderCode}
        empty={emptyText}
        actions={(ele) => orderActions(ele)}
        columns={[
          {
            key: "orderId",
            header: "Order ID",
            cell: (ele) => ele?.orderCode || "—",
          },
          {
            key: "customer",
            header: "Customer",
            cell: (ele) => ele?.customerName || "—",
          },
          {
            key: "product",
            header: "Product / design",
            cell: (ele) => {
              const lineSummary = orderLineSummary(ele);
              return (
                <>
                  <div>{ele?.productDesign || "—"}</div>
                  {lineSummary ? (
                    <div className="gi-au-hint" style={{ marginBottom: 0 }}>
                      {lineSummary}
                    </div>
                  ) : null}
                </>
              );
            },
          },
          {
            key: "due",
            header: "Due",
            hideSm: true,
            cell: (ele) => (ele?.dueDate ? moment(new Date(ele.dueDate)).format("YYYY-MM-DD") : "—"),
          },
          {
            key: "status",
            header: "Status",
            cell: (ele) => orderStatusCell(ele),
          },
          {
            key: "record",
            header: "Record",
            cell: (ele) => <span className={recordClass(ele)}>{recordLabel(ele)}</span>,
          },
          {
            key: "elapsed",
            header: "Elapsed",
            hideSm: true,
            cell: (ele) => (
              <div className="order-elapsed">
                {ele?.elapsedDays != null ? `${ele.elapsedDays}d` : "—"}
                {ele?.completedAt ? " · sent" : ""}
              </div>
            ),
          },
          ...(isSuperAdmin
            ? [
                {
                  key: "studio",
                  header: "Studio",
                  hideSm: true,
                  cell: (ele: any) => ele?.orgName || "—",
                },
              ]
            : []),
        ]}
      />
      {totPages > 1 ? (
        <div className="geo-pagination">
          <Pagination count={totPages} page={offset + 1} onChange={handlePageChange} />
        </div>
      ) : null}
    </div>
  );
};

export default OrdersList;
